/* App router — persistent retail chrome so Obchody / Organizace / Moje objednávky
 * share one QueryClient + CustomerAppShell (no full-tree remount flicker). */

import { Suspense, useMemo, type ReactNode } from 'react';
import { QueryClientProvider } from '@tanstack/react-query';
import { I18nextProvider, useTranslation } from 'react-i18next';
import { Navigate, Outlet, Route, Routes, useLocation, useParams } from 'react-router-dom';
import i18n from './i18n.js';
import { TenantProvider, useTenantCode } from './shared/tenant/TenantProvider.js';
import { CustomerAccountCacheSync } from './shared/session/CustomerAccountCacheSync.js';
import { PublicDirectorySseProvider } from './features/shops/hooks/PublicDirectorySseProvider.js';
import { CustomerDirectoryGlobalSync } from './features/shops/hooks/CustomerDirectoryGlobalSync.js';
import { ReceiptLocaleUiSync } from './app/ReceiptLocaleUiSync.js';
import { EmailLocaleUiSync } from './app/EmailLocaleUiSync.js';
import { CustomerSessionProvider } from './shared/session/CustomerSessionProvider.js';
import { ProactiveSessionRefresh } from './shared/session/ProactiveSessionRefresh.js';
import { AppDataProvider } from './shared/providers/AppDataProvider.js';
import { createCustomerQueryClient } from './shared/providers/customerQueryClient.js';
import { RuntimeConfigProvider } from './shared/providers/RuntimeConfigProvider.js';
import { CookieConsentProvider, useCookieConsent } from './features/consent/CookieConsentProvider.js';
import { CookieConsentBanner } from './features/consent/CookieConsentBanner.js';
import type { RuntimeConfig } from './config/runtimeConfig.js';
import { MarketingAnalyticsConsentModal } from './features/consent/MarketingAnalyticsConsentModal.js';
import { ConsentPromotionBridge } from './features/consent/consentPromotionBridge.js';
import { CustomerAnalyticsShell } from './shared/analytics/customerAnalyticsRouteFlow.js';
import { CustomerObservabilityProvider } from './shared/observability/CustomerObservabilityContext.js';
import { InterruptedJourneyRecoveryBootstrap } from './app/pwa/InterruptedJourneyRecoveryBootstrap.js';
import { PwaLifecycle } from './app/pwa/PwaLifecycle.js';
import { CustomerAppShell } from './app/CustomerAppShell.js';
import { CustomerBrowseRouteGuard } from './app/CustomerBrowseRouteGuard.js';
import { CustomerScanRouteGuard } from './app/CustomerScanRouteGuard.js';
import {
  isPlatformChromeTenant,
  isPlatformRetailPath,
  PLATFORM_CHROME_TENANT_CODE,
} from './app/platformChromeTenant.js';
import {
  PlatformRetailHubRoutes,
  ShopsDirectoryRoutes,
  TenantAccountRoutes,
  TenantPickerRoutes,
  TenantRouteTree,
} from './app/routes.js';
import {
  PlatformHomeRedirect,
  PlatformSignInRoute,
  PlatformSignUpRoute,
  SignUpPendingRoute,
  ConfirmEmailRoute,
  AddPhoneGateRoute,
  OnboardingSetCredentialsRoute,
  ForgotPasswordRoute,
  ResetPasswordRoute,
  Wave1Suspense,
} from './app/routes/routeHelpers.js';
import { GuestFavoritesScreen } from './features/account/GuestFavoritesScreen.js';
import { BrowsePlaceholderPage } from './features/browse/BrowsePlaceholderPage.js';
import { GuestReceiptOpenPage, PlatformScanPage } from './app/routes/lazyRouteComponents.js';
import {
  resolveIllegalPlatformTenantRedirect,
  resolveLegacyLoginPathRedirect,
  resolveReservedTenantSegmentRedirect,
} from './features/auth/customerAuthPaths.js';
import { HierarchicalBackFlowProvider } from './shared/context/HierarchicalBackFlowContext.js';
import { JourneyProvider } from './shared/context/JourneyContext.js';
import { SalesPointProvider } from './shared/context/SalesPointContext.js';
import { NotificationProvider } from './shared/context/NotificationContext.js';
import { CustomerDocumentSeo } from './shared/seo/CustomerDocumentSeo.js';

interface AppProps {
  config: RuntimeConfig;
}

function RouteLoadingFallback(): JSX.Element {
  const { t } = useTranslation('customer');
  return (
    <p className="p-4 text-sm text-[var(--color-on-surface-muted)]" role="status">
      {t('shared.loading')}
    </p>
  );
}

/**
 * Single persistent layout for platform + tenant retail routes.
 * Bottom-nav switches only swap `<Outlet />` content — chrome/providers stay mounted.
 */
function PersistentRetailLayout({ config }: AppProps): JSX.Element {
  const location = useLocation();
  const params = useParams<{ tenantCode?: string }>();
  const loginAliasRedirect = resolveLegacyLoginPathRedirect(location.pathname);
  const platformPath = isPlatformRetailPath(location.pathname);
  const urlTenant = params.tenantCode?.trim() ?? '';
  // Platform URLs keep `__platform__` chrome — ApiClient uses URL tenant only.
  let tenantCodeForProvider = PLATFORM_CHROME_TENANT_CODE;
  if (!platformPath && urlTenant.length > 0) {
    tenantCodeForProvider = urlTenant;
  }

  const cspSessionKey = platformPath ? PLATFORM_CHROME_TENANT_CODE : urlTenant;

  // `/login` is never a real tenant — redirect before API chrome mounts as login.
  if (loginAliasRedirect !== null) {
    return <Navigate to={loginAliasRedirect} replace />;
  }

  if (!platformPath && urlTenant.length === 0) {
    return <Navigate to="/" replace />;
  }

  return (
    <TenantProvider tenantCode={tenantCodeForProvider}>
      <div
        data-testid="customer-csp-remount-root"
        data-csp-key={cspSessionKey}
      >
        <CustomerSessionProvider>
          <AppDataProvider config={config}>
            <ConsentPromotionBridge />
            <CustomerObservabilityProvider>
              <PublicDirectorySseProvider>
                <CustomerAccountCacheSync />
                <CustomerDirectoryGlobalSync />
                <ReceiptLocaleUiSync />
                <EmailLocaleUiSync />
              <ProactiveSessionRefresh />
              <CookieConsentByTenant>
                <MarketingAnalyticsConsentModal />
                <TenantAnalyticsShell>
                  <InterruptedJourneyRecoveryBootstrap />
                  <CustomerAppShell>
                    {/* null fallback: avoid full-pane "loading" flash on bottom-nav tab switches */}
                    <Suspense fallback={null}>
                      <Outlet />
                    </Suspense>
                  </CustomerAppShell>
                  <CookieConsentBanner />
                  <PwaLifecycle />
                </TenantAnalyticsShell>
              </CookieConsentByTenant>
              </PublicDirectorySseProvider>
            </CustomerObservabilityProvider>
          </AppDataProvider>
        </CustomerSessionProvider>
      </div>
    </TenantProvider>
  );
}

/** Journey / sales-point state remounts on tenant URL change only (not platform tabs). */
function TenantScopedProviders(): JSX.Element {
  const location = useLocation();
  const { tenantCode } = useParams<{ tenantCode: string }>();
  const tenantSessionKey = tenantCode?.trim() ?? '';
  if (tenantSessionKey.length === 0) {
    return <Navigate to="/" replace />;
  }
  /* `__platform__` is chrome-only — never a real `/:tenantCode` URL. */
  if (isPlatformChromeTenant(tenantSessionKey)) {
    return (
      <Navigate to={resolveIllegalPlatformTenantRedirect(location.pathname)} replace />
    );
  }
  const reservedRedirect = resolveReservedTenantSegmentRedirect(
    tenantSessionKey,
    location.pathname,
  );
  if (reservedRedirect !== null) {
    return <Navigate to={reservedRedirect} replace />;
  }
  return (
    <JourneyProvider key={tenantSessionKey}>
      <SalesPointProvider key={tenantSessionKey}>
        <NotificationProvider>
          <Outlet />
        </NotificationProvider>
      </SalesPointProvider>
    </JourneyProvider>
  );
}

export function App({ config }: AppProps): JSX.Element {
  const queryClient = useMemo(() => createCustomerQueryClient(), []);

  return (
    <I18nextProvider i18n={i18n}>
      <CustomerDocumentSeo />
      <QueryClientProvider client={queryClient}>
        <RuntimeConfigProvider config={config}>
          <Routes>
            <Route element={<PersistentRetailLayout config={config} />}>
              <Route index element={<PlatformHomeRedirect />} />
              <Route path="orgs" element={<TenantPickerRoutes />} />
              <Route path="shops" element={<ShopsDirectoryRoutes />} />
              <Route
                path="browse/*"
                element={
                  <CustomerBrowseRouteGuard loadingFallback={<RouteLoadingFallback />}>
                    <Suspense fallback={<RouteLoadingFallback />}>
                      <BrowsePlaceholderPage />
                    </Suspense>
                  </CustomerBrowseRouteGuard>
                }
              />
              <Route
                path="favorites"
                element={
                  <Suspense fallback={<RouteLoadingFallback />}>
                    <GuestFavoritesScreen />
                  </Suspense>
                }
              />
              <Route
                path="scan"
                element={
                  <CustomerScanRouteGuard loadingFallback={<RouteLoadingFallback />}>
                    <Suspense fallback={<RouteLoadingFallback />}>
                      <PlatformScanPage />
                    </Suspense>
                  </CustomerScanRouteGuard>
                }
              />
              <Route
                path="receipt-recovery"
                element={
                  <Suspense fallback={<RouteLoadingFallback />}>
                    <GuestReceiptOpenPage />
                  </Suspense>
                }
              />
              <Route
                path="sign-in"
                element={
                  <Wave1Suspense>
                    <PlatformSignInRoute />
                  </Wave1Suspense>
                }
              />
              {/* Platform registration form — org select optional (no org = platform-only). */}
              <Route
                path="sign-up"
                element={
                  <Wave1Suspense>
                    <PlatformSignUpRoute />
                  </Wave1Suspense>
                }
              />
              <Route
                path="sign-up/pending"
                element={
                  <Wave1Suspense>
                    <SignUpPendingRoute />
                  </Wave1Suspense>
                }
              />
              <Route
                path="confirm-email"
                element={
                  <Wave1Suspense>
                    <ConfirmEmailRoute />
                  </Wave1Suspense>
                }
              />
              {/* Wave 4.6 G9 — sibling of ConfirmEmail only; OUTSIDE TenantAccountRoutes RequireCustomerAuth. */}
              <Route
                path="account/add-phone"
                element={
                  <Wave1Suspense>
                    <AddPhoneGateRoute />
                  </Wave1Suspense>
                }
              />
              <Route
                path="onboarding/set-credentials"
                element={
                  <Wave1Suspense>
                    <OnboardingSetCredentialsRoute />
                  </Wave1Suspense>
                }
              />
              <Route
                path="forgot-password"
                element={
                  <Wave1Suspense>
                    <ForgotPasswordRoute />
                  </Wave1Suspense>
                }
              />
              <Route
                path="reset-password"
                element={
                  <Wave1Suspense>
                    <ResetPasswordRoute />
                  </Wave1Suspense>
                }
              />
              {/* `/login` is a tenant-less alias — never `tenantCode=login`. */}
              <Route path="login" element={<Navigate to="/sign-in" replace />} />
              <Route path="login/*" element={<Navigate to="/sign-in" replace />} />
              {/* Wave C — platform account hubs (`TenantProvider` = `__platform__`). */}
              {TenantAccountRoutes()}
              {/* Wave D — platform overflow hubs (same pages as tenant retail). */}
              {PlatformRetailHubRoutes()}
              <Route path=":tenantCode" element={<TenantScopedProviders />}>
                {TenantRouteTree()}
              </Route>
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </RuntimeConfigProvider>
      </QueryClientProvider>
    </I18nextProvider>
  );
}

/**
 * Test harness only (G17 AC12 R6 CSP remount RTL) — not used by production `App`.
 * May wrap `HierarchicalBackFlowProvider` for isolated mounts; production single
 * provider lives exclusively on `CustomerAppShell` under `PersistentRetailLayout`.
 */
export function TenantScopedShell({ config }: AppProps): JSX.Element {
  const { tenantCode: tenantCodeParam } = useParams<{ tenantCode: string }>();
  const tenantSessionKey = tenantCodeParam?.trim() ?? '';
  const queryClient = useMemo(() => createCustomerQueryClient(), []);
  const cspSessionKey = tenantSessionKey;

  return (
    <QueryClientProvider client={queryClient}>
      <RuntimeConfigProvider config={config}>
        <TenantProvider>
          <div
            data-testid="customer-csp-remount-root"
            data-csp-key={cspSessionKey}
          >
            <CustomerSessionProvider>
              <JourneyProvider key={tenantSessionKey}>
                <HierarchicalBackFlowProvider key={tenantSessionKey}>
                  <SalesPointProvider key={tenantSessionKey}>
                    <NotificationProvider>
                      <AppDataProvider config={config}>
                        <ConsentPromotionBridge />
                        <CustomerObservabilityProvider>
                          <CustomerAccountCacheSync />
                          <ReceiptLocaleUiSync />
                          <EmailLocaleUiSync />
                          <ProactiveSessionRefresh />
                          <CookieConsentByTenant>
                            <MarketingAnalyticsConsentModal />
                            <TenantAnalyticsShell>
                              <InterruptedJourneyRecoveryBootstrap />
                              <Suspense fallback={<RouteLoadingFallback />}>
                                <Routes>{TenantRouteTree()}</Routes>
                              </Suspense>
                              <CookieConsentBanner />
                              <PwaLifecycle />
                            </TenantAnalyticsShell>
                          </CookieConsentByTenant>
                        </CustomerObservabilityProvider>
                      </AppDataProvider>
                    </NotificationProvider>
                  </SalesPointProvider>
                </HierarchicalBackFlowProvider>
              </JourneyProvider>
            </CustomerSessionProvider>
          </div>
        </TenantProvider>
      </RuntimeConfigProvider>
    </QueryClientProvider>
  );
}

function CookieConsentByTenant({ children }: { children: ReactNode }): JSX.Element {
  const tenantCode = useTenantCode();
  return (
    <CookieConsentProvider key={tenantCode}>
      {children}
    </CookieConsentProvider>
  );
}

function TenantAnalyticsShell({ children }: { children: ReactNode }): JSX.Element {
  const cookieConsent = useCookieConsent();

  return (
    <CustomerAnalyticsShell
      analyticsConsentGranted={cookieConsent.decision?.analytics === true}
      analyticsConsentVersion={cookieConsent.decision?.version}
      analyticsConsentDecidedAtMs={cookieConsent.decision?.decidedAtMs ?? null}
    >
      {children}
    </CustomerAnalyticsShell>
  );
}
